'use client';

import { motion } from "framer-motion";
import Link from "next/link";
import { ArrowLeft, CheckCircle, Truck, MapPin, Clock, Fuel } from "lucide-react";
import { useParams } from "next/navigation";

const fleetData = {
  title: "Fleet Management Dashboard",
  subtitle: "Real-time vehicle tracking and optimization system for efficient fleet operations.",
  headerGradient: "bg-gradient-to-r from-blue-500 via-indigo-600 to-purple-600",
  accentColor: "text-blue-600",
  buttonColor: "bg-blue-600 hover:bg-blue-700",
  challenge: "Fleet operators need comprehensive visibility into vehicle locations, driver behavior, maintenance schedules, and operational efficiency to reduce costs and improve service delivery.",
  solutionHighlights: [
    "Real-time GPS tracking and route optimization for improved delivery efficiency",
    "Driver behavior monitoring with safety analytics and scoring systems",
    "Predictive maintenance alerts based on vehicle diagnostics and usage patterns",
    "Fuel consumption optimization and cost analysis reporting"
  ],
  benefits: [
    "30% reduction in fuel costs through route optimization",
    "25% improvement in delivery times",
    "40% decrease in maintenance-related downtime",
    "Enhanced driver safety and compliance monitoring"
  ],
  techStack: ["IoT Sensors", "GPS Tracking", "Machine Learning", "Real-time Analytics", "Mobile Apps", "Cloud Platform"],
  fleetStats: {
    activeVehicles: "247",
    avgFuelEfficiency: "7.2L/100km",
    onTimeDeliveries: "94%",
    maintenanceAlerts: "12"
  }
};

export default function FleetManagementDashboardPage() {
  const params = useParams();
  const currentLocale = (params?.locale as string) || 'en';
  
  return (
    <div className="min-h-screen bg-slate-50 text-slate-800">
      {/* Header Section */}
      <header className={`py-16 md:py-24 text-white ${fleetData.headerGradient}`}>
        <div className="container mx-auto max-w-4xl px-4 text-center">
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
            <Link href={`/${currentLocale}/portfolio`} className="inline-flex items-center text-sm text-white hover:text-indigo-100 transition-colors duration-300 bg-white/10 hover:bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full shadow-md">
              <ArrowLeft size={18} className="mr-2" />
              Back to Portfolio
            </Link>
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
            {fleetData.title}
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="text-lg md:text-xl text-indigo-100 max-w-3xl mx-auto">
            {fleetData.subtitle}
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="mt-8 flex flex-wrap gap-3 justify-center">
            {fleetData.techStack.slice(0, 3).map(tech => (
              <span key={tech} className="bg-white/20 text-white px-3 py-1 rounded-full text-xs font-medium">
                {tech}
              </span>
            ))}
          </motion.div>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="container mx-auto max-w-5xl px-4 py-12 md:py-16">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-white p-8 md:p-12 rounded-xl shadow-xl -mt-20 md:-mt-32 relative z-10">
          
          {/* Fleet Dashboard Visual Section */}
          <section className="mb-12">
            <h2 className={`text-3xl font-semibold mb-8 text-center ${fleetData.accentColor}`}>Fleet Control Center</h2>
            <div className="bg-slate-100 p-4 sm:p-6 rounded-lg shadow-lg border border-slate-200">
              <div className="flex items-center justify-between px-4 py-2 mb-4 bg-gradient-to-r from-slate-700 to-slate-800 text-white text-sm font-semibold rounded-t-md shadow">
                <span>Fleet Management System</span>
                <span className="text-xs bg-blue-500/90 px-2 py-0.5 rounded-full flex items-center">
                  <Truck size={12} className="mr-1"/>
                  Live Tracking
                </span>
              </div>
              <div className="flex flex-col md:flex-row gap-4 min-h-[280px]">
                {/* Fleet Map Simulation */}
                <div className="flex-1 flex items-center justify-center bg-slate-200 rounded-md p-4 relative min-h-[200px]">
                  <div className="w-full h-full bg-slate-800 rounded-lg relative overflow-hidden shadow-inner border border-slate-700 flex flex-col p-4">
                    <div className="absolute top-2 left-2 right-2 flex justify-between items-center">
                      <div className="flex items-center">
                        <MapPin size={18} className="text-blue-400 mr-1" />
                        <span className="text-slate-200 font-medium">Fleet Overview</span>
                      </div>
                      <div className="bg-blue-900/50 text-blue-200 px-2 py-0.5 rounded text-xs font-medium">
                        Live View
                      </div>
                    </div>
                    
                    <div className="mt-10 flex-1 relative">
                      {/* Simulated map with vehicle markers */}
                      <div className="w-full h-full bg-slate-700 rounded relative">
                        {/* Route lines */}
                        <svg className="absolute inset-0 w-full h-full">
                          <path d="M50 50 Q100 80 150 60 T250 70" stroke="#3b82f6" strokeWidth="2" fill="none" opacity="0.6"/>
                          <path d="M30 120 Q80 140 130 110 T230 130" stroke="#10b981" strokeWidth="2" fill="none" opacity="0.6"/>
                        </svg>
                        
                        {/* Vehicle markers */}
                        <div className="absolute top-12 left-12 w-3 h-3 bg-blue-400 rounded-full animate-pulse">
                          <div className="absolute -top-1 -left-1 w-5 h-5 border-2 border-blue-400 rounded-full animate-ping opacity-25"></div>
                        </div>
                        <div className="absolute top-24 left-32 w-3 h-3 bg-green-400 rounded-full animate-pulse">
                          <div className="absolute -top-1 -left-1 w-5 h-5 border-2 border-green-400 rounded-full animate-ping opacity-25"></div>
                        </div>
                        <div className="absolute bottom-16 right-20 w-3 h-3 bg-yellow-400 rounded-full animate-pulse">
                          <div className="absolute -top-1 -left-1 w-5 h-5 border-2 border-yellow-400 rounded-full animate-ping opacity-25"></div>
                        </div>
                        
                        {/* Legend */}
                        <div className="absolute bottom-2 left-2 text-xs text-slate-300 space-y-1">
                          <div className="flex items-center">
                            <div className="w-2 h-2 bg-blue-400 rounded-full mr-2"></div>
                            <span>En Route</span>
                          </div>
                          <div className="flex items-center">
                            <div className="w-2 h-2 bg-green-400 rounded-full mr-2"></div>
                            <span>Delivered</span>
                          </div>
                          <div className="flex items-center">
                            <div className="w-2 h-2 bg-yellow-400 rounded-full mr-2"></div>
                            <span>Maintenance</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Sidebar Fleet Stats */}
                <div className="w-full md:w-52 bg-slate-50 rounded-md p-4 space-y-2.5 ring-1 ring-slate-200 flex flex-col justify-center">
                  {[ 
                    { label: "Active Vehicles", value: fleetData.fleetStats.activeVehicles, color: "text-blue-700", bgColor:"bg-blue-100", icon: Truck }, 
                    { label: "Avg Fuel Efficiency", value: fleetData.fleetStats.avgFuelEfficiency, color: "text-green-700", bgColor:"bg-green-100", icon: Fuel }, 
                    { label: "On-Time Deliveries", value: fleetData.fleetStats.onTimeDeliveries, color: "text-amber-700", bgColor:"bg-amber-100", icon: Clock }, 
                    { label: "Maintenance Alerts", value: fleetData.fleetStats.maintenanceAlerts, color: "text-red-700", bgColor:"bg-red-100", icon: MapPin } 
                  ].map(stat => (
                    <div key={stat.label} className={`flex items-center justify-between p-2 rounded ${stat.bgColor}`}>
                      <div className="flex items-center">
                        <stat.icon size={14} className={`${stat.color} mr-1`} />
                        <span className={`text-xs font-medium ${stat.color}`}>{stat.label}</span>
                      </div>
                      <span className={`font-bold text-sm ${stat.color}`}>{stat.value}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="px-4 py-1.5 mt-3 bg-slate-200 border-t border-slate-300 text-xs text-slate-600 flex items-center gap-2 rounded-b-md">
                <span className="w-2 h-2 rounded-full bg-blue-500 inline-block animate-pulse"></span>
                <span>Fleet tracking active. Last update: 15 seconds ago.</span>
              </div>
            </div>
          </section>

          <div className="grid md:grid-cols-1 gap-10">
            <section>
              <h2 className={`text-2xl font-semibold mb-4 ${fleetData.accentColor}`}>The Challenge</h2>
              <p className="text-slate-700 leading-relaxed whitespace-pre-line">
                {fleetData.challenge}
              </p>
            </section>

            <section>
              <h2 className={`text-2xl font-semibold mb-4 ${fleetData.accentColor}`}>Our Solution</h2>
              <ul className="space-y-3 text-slate-700">
                {fleetData.solutionHighlights.map((item, index) => (
                  <motion.li key={index} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: index * 0.1 }} className="flex items-start pl-2">
                    <CheckCircle className={`w-5 h-5 ${fleetData.accentColor} mr-3 mt-0.5 flex-shrink-0`} />
                    <span>{item}</span>
                  </motion.li>
                ))}
              </ul>
            </section>

            <section>
              <h2 className={`text-2xl font-semibold mb-4 ${fleetData.accentColor}`}>Key Benefits</h2>
              <div className="bg-blue-50 border border-blue-200 p-6 rounded-lg shadow-sm">
                <ul className="space-y-3">
                  {fleetData.benefits.map((benefit, index) => (
                    <motion.li key={index} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: index * 0.1 }} className="flex items-start text-blue-800">
                      <CheckCircle className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                      <span>{benefit}</span>
                    </motion.li>
                  ))}
                </ul>
              </div>
            </section>

            <section>
              <h2 className={`text-2xl font-semibold mb-5 ${fleetData.accentColor}`}>Technologies Used</h2>
              <div className="flex flex-wrap gap-3">
                {fleetData.techStack.map((tech, index) => (
                  <motion.span key={index} initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: index * 0.05 }}
                    className="bg-slate-200 text-slate-700 px-4 py-1.5 rounded-full text-sm font-medium border border-slate-300 hover:bg-slate-300 transition-colors cursor-default">
                    {tech}
                  </motion.span>
                ))}
              </div>
            </section>
          </div>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }} className="mt-16 text-center">
            <Link href={`/${currentLocale}/contact`} className={`${fleetData.buttonColor} text-white font-semibold py-3.5 px-10 rounded-lg text-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50`}>
              Optimize Your Fleet Operations
            </Link>
          </motion.div>

        </motion.div>
      </main>
    </div>
  );
} 